/**
 * ChatWidget — root container component.
 *
 * Composes ChatHeader, MessageList, ChatInput, and ToggleButton.
 * Manages open/close state and responsive layout transitions.
 * This is the top-level component mounted inside Shadow DOM.
 */

import type {
  ResolvedWidgetConfig,
  WidgetState,
  WidgetSize,
  ViewportMode,
  ConsentStatus,
  ConsentEvent,
  ConsentCallbacks,
  ConsentRequestMessage,
  SuggestedAction,
  SuggestedActionsDisplay,
  SuggestedActionAnalyticsEvent,
  FeedbackRequestMessage,
  FeedbackEvent,
  FeedbackCallbacks,
  FormMessage,
  FormFieldDefinition,
  FormSubmitEvent,
} from "../types";
import { ToggleButton } from "./ToggleButton";
import { ChatHeader } from "./ChatHeader";
import { MessageList } from "./MessageList";
import { ChatInput } from "./ChatInput";
import { ConsentPrompt } from "./ConsentPrompt";
import { FeedbackCapture } from "./FeedbackCapture";
import { SuggestedActions } from "./SuggestedActions";
import { InlineForm } from "./InlineForm";
import {
  decideFormDisposition,
  fieldsToConversationalPrompts,
} from "../services/formModeDispatcher";
import { applyBranding } from "../utils/applyBranding";
import { buildFormAckSummary } from "../utils/formAck";
import {
  TransportLayer,
  TransportState,
  type ChatMessage as WireChatMessage,
} from "../services/transport";
import {
  readPanelState,
  readTicket,
  storePanelState,
} from "../services/resumeTicket";

export class ChatWidget {
  private static readonly OPENING_TERMS_ACKNOWLEDGEMENT = "OK";

  private static readonly OPAQUE_PILL_ACTION_TOKENS = new Set([
    "book_appointment",
    "view_hours",
    "view_packages",
    "view_locations",
    "view_specialists",
    "transfer_to_human",
  ]);

  private state: WidgetState = "closed";
  private viewportMode: ViewportMode = "desktop";
  private consentStatus: ConsentStatus = "pending";
  private readonly requiresOpeningTermsAcknowledgement: boolean;
  private openingTermsAcknowledged = false;
  /** REQ-A-020 / #880 — a replayed transcript is landing in this panel. */
  private resumedConversation = false;
  /**
   * REQ-D-046 — current size state. Initialised from
   * `config.defaultSize` (with localStorage override per entity if
   * persisted by a prior visit). Drives panel-layout CSS classes +
   * header chrome.
   */
  private size: WidgetSize;
  /**
   * REQ-D-046 — the trigger element the modal-mode user clicked to
   * open the chat. Stored so we can return focus there on close per
   * the WCAG focus-restore pattern.
   */
  private modalTrigger: HTMLElement | null = null;
  /**
   * REQ-D-046 — backdrop overlay element for modal mode. Owned by the
   * widget so we can mount/unmount it as the modal opens/closes.
   */
  private modalBackdrop: HTMLElement | null = null;
  /**
   * REQ-D-046 — bound click handler for the trigger CTA. Saved so we
   * can remove it cleanly on destroy() — leaking listeners would
   * outlive the widget when an embed swaps between mounts.
   */
  private boundTriggerHandler: ((evt: Event) => void) | null = null;
  /** REQ-D-046 — the actual queried trigger element (may be null). */
  private triggerCtaEl: HTMLElement | null = null;
  private readonly mediaQuery: MediaQueryList;
  private readonly container: HTMLElement;

  readonly toggle: ToggleButton;
  readonly header: ChatHeader;
  readonly messageList: MessageList;
  private _chatInput: ChatInput;

  private readonly panelEl: HTMLElement;
  private readonly hostEl: HTMLElement;

  /** External callbacks for consent events. Set via onConsent(). */
  private consentCallbacks: ConsentCallbacks | null = null;

  /** Current suggested actions instance (null when no pills are showing) */
  private suggestedActions: SuggestedActions | null = null;

  /** External callback for suggested action pill clicks */
  private suggestedActionCallback: ((action: string) => void) | null = null;

  /** External callback for suggested action analytics events */
  private suggestedActionAnalyticsCallback:
    ((event: SuggestedActionAnalyticsEvent) => void) | null = null;

  /** Confidence threshold for displaying suggested action pills */
  private suggestedActionsThreshold = 0.6;

  /** External callback for feedback events. Set via onFeedbackSubmit(). */
  private feedbackCallbacks: FeedbackCallbacks | null = null;

  /**
   * Transport layer for backend communication. Constructed in the
   * constructor (production wiring — M01-T02). Null when wsUrl /
   * restUrl cannot be derived from config.apiEndpoint.
   */
  private transport: TransportLayer | null = null;

  /**
   * REQ-C-033 — message_ids of form frames already rendered this
   * session. The backend's reconnect replay buffer re-sends buffered
   * `form` frames on every WS reconnect (per the Shard-1a contract);
   * without this guard the same form renders as N stacked InlineForms.
   * Mirrors MessageList.appendChatMessage's data-message-id dedup, but
   * forms are not ChatMessages so the guard lives here.
   */
  private readonly renderedFormIds: Set<string> = new Set();

  /**
   * REQ-C-033 — message_ids of form frames whose InlineForm has already
   * been SUBMITTED this session. A replayed frame for a submitted form
   * re-renders in a disabled state (no inputs editable, no submit) so
   * the patient cannot double-submit after a reconnect.
   */
  private readonly submittedFormIds: Set<string> = new Set();

  constructor(
    hostElement: HTMLElement,
    private readonly config: ResolvedWidgetConfig,
  ) {
    this.hostEl = hostElement;
    this.container = document.createElement("div");
    this.container.className = "hana-widget-root";

    // REQ-D-046 — resolve initial size: persisted-per-entity-in-this-
    // origin override > config.defaultSize. The persistence key is
    // scoped to the entity so a tester switching from /uat-sites/eec
    // to /uat-sites/dccs doesn't inherit the wrong widget footprint.
    // Modal mode ignores persistence — modals are always sized by the
    // modal CSS tokens, the panel size state machine only fires for
    // panel mode.
    this.size =
      config.uiMode === "panel" && config.allowExpand
        ? (this.restorePersistedSize() ?? config.defaultSize)
        : config.defaultSize;

    // Responsive detection via matchMedia
    this.mediaQuery = window.matchMedia(
      `(max-width: ${config.mobileBreakpoint}px)`,
    );
    this.viewportMode = this.mediaQuery.matches ? "mobile" : "desktop";
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.mediaQuery.addEventListener("change", this.handleViewportChange);
    this.updateViewportClass();
    this.requiresOpeningTermsAcknowledgement =
      this.getConfiguredStarterPrompts()[0]?.toUpperCase() ===
      ChatWidget.OPENING_TERMS_ACKNOWLEDGEMENT;

    // Toggle button
    this.toggle = new ToggleButton({
      position: config.position,
      onClick: () => this.togglePanel(),
      launcherIconUrl: config.branding.launcherIconUrl,
      apiEndpoint: config.apiEndpoint,
    });

    // Chat panel
    this.panelEl = document.createElement("div");
    this.panelEl.className = "hana-panel";
    this.panelEl.setAttribute("role", "dialog");
    this.panelEl.setAttribute("aria-label", "Chat with HANA");
    this.panelEl.setAttribute("aria-hidden", "true");

    // REQ-D-046 — mode + size classes drive the panel layout. Default
    // (panel + expanded) renders the legacy Wave-1 footprint; modal
    // mode swaps to centred-overlay positioning + adds aria-modal.
    this.panelEl.classList.add(`hana-panel--mode-${config.uiMode}`);
    this.panelEl.classList.add(`hana-panel--size-${this.size}`);
    if (config.uiMode === "modal") {
      this.panelEl.setAttribute("aria-modal", "true");
    }

    if (config.position === "bottom-left") {
      this.panelEl.classList.add("hana-panel--left");
    }

    // Header — wired to the size state machine when allowExpand is on.
    this.header = new ChatHeader({
      config,
      onClose: () => this.close(),
      onToggleSize: config.allowExpand ? () => this.toggleSize() : undefined,
      initialSize: this.size,
    });

    // Message list
    this.messageList = new MessageList({
      config,
      initialSuggestedPrompts: this.requiresOpeningTermsAcknowledgement
        ? [ChatWidget.OPENING_TERMS_ACKNOWLEDGEMENT]
        : undefined,
      callbacks: {
        // REQ-D-046 — a tapped static empty-state starter pill usually
        // sends a natural-language patient question as the first user
        // turn. The beta opening "OK" action is the exception: it is a
        // local Terms-of-Use acknowledgement gate and MUST NOT be sent
        // to orchestration.
        onSuggestedPrompt: (prompt: string) => {
          void this.handleUserSend(prompt, "starter_prompt");
        },
      },
    });

    // Chat input
    this._chatInput = new ChatInput({
      onSend: (message: string) => this.handleUserSend(message),
      placeholder: this.requiresOpeningTermsAcknowledgement
        ? 'Type "OK" to proceed'
        : undefined,
    });

    // Assemble panel
    this.panelEl.appendChild(this.header.getElement());
    this.panelEl.appendChild(this.messageList.getElement());
    this.panelEl.appendChild(this._chatInput.getElement());

    // Assemble container
    this.container.appendChild(this.toggle.getElement());
    this.container.appendChild(this.panelEl);

    // Apply branding CSS custom properties
    applyBranding(this.hostEl, config);

    // Keyboard handling: Escape closes the panel, Tab traps focus
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.trapFocus = this.trapFocus.bind(this);

    // Initialise transport (M01-T02 — production WebSocket wiring with
    // automatic REST fallback). Constructed eagerly so the widget can
    // connect on first open and replay queued messages over the
    // transport layer instead of dropping them.
    this.transport = this.buildTransport();

    // REQ-D-046 — the backdrop overlay is modal-only: panel mode is a
    // docked surface, it does not dim the host page.
    if (config.uiMode === "modal") {
      this.modalBackdrop = document.createElement("div");
      this.modalBackdrop.className = "hana-modal-backdrop";
      this.modalBackdrop.setAttribute("aria-hidden", "true");
      this.modalBackdrop.addEventListener("click", () => this.close());
      this.container.appendChild(this.modalBackdrop);
    }

    // #857 — the CTA binding MUST be called outside the modal branch.
    // bindTriggerCta() itself is mode-independent, but leaving the call
    // inside `if (uiMode === "modal")` left panel mode never invoking
    // it: the bubble stayed visible and the host CTA stayed dead, which
    // is the whole bug. If the selector misses we log an error and fall
    // back to the floating-bubble launcher so users are NEVER blocked
    // from reaching the chat.
    this.bindTriggerCta();
  }

  /**
   * REQ-D-046 — locate the host-page CTA referenced by
   * `config.triggerCtaSelector` and wire its click handler. On miss,
   * surface a console.error (so the embedder sees the wiring gap)
   * and leave the floating-bubble launcher visible so the chat
   * remains reachable.
   *
   * Works in both modal and panel modes so embedders can supply
   * their own launcher button regardless of ui-mode.
   */
  private bindTriggerCta(): void {
    const selector = this.config.triggerCtaSelector;
    if (!selector) {
      if (this.config.uiMode === "modal") {
        console.error(
          "[HANA Widget] modal mode requires data-trigger-cta-selector; " +
            "falling back to floating bubble launcher.",
        );
      }
      // No custom trigger — keep the floating launcher visible.
      return;
    }
    let el: Element | null = null;
    try {
      el = document.querySelector(selector);
    } catch (err) {
      console.error(
        "[HANA Widget] invalid CSS selector for data-trigger-cta-selector:",
        selector,
        err,
      );
      return;
    }
    if (!el || !(el instanceof HTMLElement)) {
      console.error(
        `[HANA Widget] data-trigger-cta-selector "${selector}" matched no ` +
          "element; falling back to floating bubble launcher.",
      );
      return;
    }
    // Selector matched — hide the floating launcher so users only see
    // the host's CTA, and route clicks through us.
    this.triggerCtaEl = el;
    this.toggle.getElement().classList.add("hana-toggle--hidden");
    this.boundTriggerHandler = (evt: Event) => {
      evt.preventDefault();
      // Remember the launcher so close() can restore focus there per
      // WCAG 2.4.3 focus-order.
      this.modalTrigger = el as HTMLElement;
      this.open();
    };
    el.addEventListener("click", this.boundTriggerHandler);
  }

  /**
   * Construct a TransportLayer from the resolved widget config. Returns
   * null when config.apiEndpoint cannot be parsed into ws:// + http://
   * pair (defensive — the apiEndpoint default is always a valid URL,
   * but if the embedder passes garbage we fail loud at first send
   * rather than silently dropping messages).
   */
  private buildTransport(): TransportLayer | null {
    let parsed: URL;
    try {
      parsed = new URL(this.config.apiEndpoint);
    } catch {
      console.error(
        "[HANA Widget] apiEndpoint is not a valid URL; transport disabled",
        this.config.apiEndpoint,
      );
      return null;
    }
    // ws:// for http://, wss:// for https://. The path is the
    // backend's /ws/chat endpoint (mounted by hana.gateway.
    // websocket_endpoint.mount_websocket).
    const wsScheme = parsed.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsScheme}//${parsed.host}/ws/chat`;
    const restUrl = `${parsed.protocol}//${parsed.host}`;

    const transport = new TransportLayer({
      wsUrl,
      restUrl,
      entityId: this.config.entityId,
      harnessSessionId: this.config.harnessSessionId,
      testHarness: this.config.testHarness,
    });
    transport.onMessage((msg) => this.handleTransportMessage(msg));
    transport.onStateChange((state) => this.handleTransportState(state));
    return transport;
  }

  /**
   * GH #792: Tear down the current transport and build a fresh one so
   * the next message goes to a new session. The old messages stay in
   * the DOM (already rendered). The new session will have patient name
   * and phone pre-populated from the old session by the backend.
   */
  private reconnectForNewSession(): void {
    if (this.transport) {
      this.transport.disconnect();
    }
    this.transport = this.buildTransport();
  }

  private getConfiguredStarterPrompts(): string[] {
    return (this.config.branding.suggestedPrompts ?? [])
      .map((prompt) => prompt.trim())
      .filter((prompt) => prompt.length > 0);
  }

  private getPostAcknowledgementStarterPrompts(): string[] {
    return this.getConfiguredStarterPrompts().filter(
      (prompt) =>
        prompt.toUpperCase() !== ChatWidget.OPENING_TERMS_ACKNOWLEDGEMENT,
    );
  }

  private isOpeningTermsAcknowledgement(text: string): boolean {
    return (
      text.trim().toUpperCase() === ChatWidget.OPENING_TERMS_ACKNOWLEDGEMENT
    );
  }

  private acknowledgeOpeningTerms(): void {
    this.openingTermsAcknowledged = true;
    this.messageList.replaceSuggestedPrompts(
      this.getPostAcknowledgementStarterPrompts(),
    );
    this._chatInput.setPlaceholder("Type a message...");
  }

  private renderOpeningTermsReminder(): void {
    this.messageList.appendChatMessage({
      id: generateMessageId(),
      type: "system",
      sender: "system",
      content:
        'Please reply "OK" to proceed after reading and accepting our Terms of Use.',
      timestamp: Date.now(),
    });
    this.messageList.replaceSuggestedPrompts([
      ChatWidget.OPENING_TERMS_ACKNOWLEDGEMENT,
    ]);
  }

  /**
   * User typed a message and pressed Enter (or Send button). Render
   * the user bubble immediately (optimistic UI) and forward the wire
   * message to the transport layer. Connection is lazy — the first
   * send triggers connect() if the widget hasn't connected yet.
   *
   * ``source`` attributes the message origin:
   * - ``"typed"`` — free-text the patient typed (the default).
   * - ``"pill"`` — REQ-D-029 suggested-ACTION pill: a discrete action
   *   token (e.g. ``book_appointment``). The backend WS handler gates
   *   pill-sourced sends against the server-side action-token allowlist
   *   (ui-backend-defense.md MUST 1+2+3) — a non-allowlisted token is
   *   REJECTED, never dispatched to orchestration.
   * - ``"starter_prompt"`` — REQ-D-046 empty-state starter prompt: a
   *   natural-language patient question (e.g. "Where is the Eagle Eye
   *   Centre?"). This is free-text patient input, NOT an action token;
   *   it MUST NOT carry ``source="pill"`` (that routes it into the
   *   action-token allowlist gate, which rejects free-text and the
   *   tester gets silence — the live defect this value fixes). The
   *   backend treats any non-``"pill"`` source as free text and flows
   *   it to the orchestrator exactly like typed input, while the
   *   distinct value preserves the tap-vs-typed analytics signal.
   */
  private async handleUserSend(
    text: string,
    source: "typed" | "pill" | "starter_prompt" = "typed",
    displayText = text,
  ): Promise<void> {
    if (text.length === 0) return;

    if (
      this.requiresOpeningTermsAcknowledgement &&
      !this.openingTermsAcknowledged
    ) {
      if (this.isOpeningTermsAcknowledgement(text)) {
        this.acknowledgeOpeningTerms();
      } else {
        this.renderOpeningTermsReminder();
      }
      return;
    }

    if (source !== "pill") {
      this.dismissSuggestedActions();
    }

    const now = Date.now();
    const messageId = generateMessageId();

    // Optimistic UI: render the user bubble immediately.
    this.messageList.appendChatMessage({
      id: messageId,
      type: "text",
      sender: "user",
      content: displayText,
      timestamp: now,
      status: "sending",
    });

    if (!this.transport) {
      // Defensive — buildTransport() returned null. Surface as a
      // system message so the user sees something rather than a
      // silent drop.
      this.messageList.appendChatMessage({
        id: generateMessageId(),
        type: "system",
        sender: "system",
        content:
          "Connection unavailable. Please reload the page and try again.",
        timestamp: Date.now(),
      });
      return;
    }

    // Connect lazily on first send. transport.send() queues the
    // message internally if the WS handshake is still in flight,
    // and replays the queue once CONNECTED_WS / CONNECTED_REST is
    // reached.
    if (this.transport.state === TransportState.DISCONNECTED) {
      void this.transport.connect();
    }

    const wire: WireChatMessage = {
      type: "user_message",
      payload: { text, source },
      message_id: messageId,
      timestamp: now / 1000, // backend protocol uses unix epoch SECONDS
      metadata: {},
    };
    try {
      await this.transport.send(wire);
      // Transport accepted the frame — flip the user-bubble status
      // from the spinner ("sending") to a single check ("sent") so
      // the spinner does not spin forever when the WS handshake is
      // lazy. The "delivered" state is set in handleTransportMessage
      // when the first bot frame for this conversation lands.
      this.messageList.setUserMessageStatus(messageId, "sent");
    } catch (err: any) {
      // GH #792: if the backend says the session has moved to WhatsApp,
      // show the transition notice. The patient can continue chatting —
      // the next send will create a new session automatically.
      if (err?.whatsappTransitioned) {
        this.messageList.appendTransitionNotice(err.message);
      } else {
        console.error("[HANA Widget] transport.send failed", err);
      }
    }
  }

  /**
   * Convert a wire ChatMessage into the widget's UI ChatMessage and
   * render it. Maps protocol message types to UI components:
   *
   *   bot_response → text bubble
   *   typing       → typing indicator (show/hide)
   *   system       → system bubble
   *   error        → system bubble with error styling
   *   ping/pong    → no UI (transport handles)
   */
  private handleTransportMessage(msg: WireChatMessage): void {
    // REQ-D-046 — when a form-mode message arrives AND the embedder
    // opted in via allow-expand, auto-expand the panel from compact
    // to expanded so the inline form has room. Pure UX assist; the
    // user can manually collapse back via the header toggle.
    if (
      msg.type === "form" &&
      this.config.uiMode === "panel" &&
      this.config.allowExpand &&
      this.size === "compact"
    ) {
      this.setSize("expanded");
    }
    if (msg.type === "form") {
      this.handleFormMessage(msg);
      return;
    }
    if (msg.type === "typing") {
      const isTyping = Boolean(
        (msg.payload as { is_typing?: boolean }).is_typing,
      );
      if (isTyping) {
        this.messageList.showTyping();
        this.dismissSuggestedActions();
      } else {
        this.messageList.hideTyping();
      }
      return;
    }
    if (msg.type === "bot_response") {
      // Dismiss the typing indicator on ANY bot_response arrival.
      // On WebSocket the server sends an explicit typing_off frame,
      // but REST polling never sends typing frames — without this,
      // the indicator stays stuck after a long orchestration turn.
      this.messageList.hideTyping();

      const payload = (msg.payload ?? {}) as {
        text?: string;
        pre_messages?: unknown;
        photo_url?: unknown;
        suggested_actions?: unknown;
        display?: unknown;
      };
      const text = String(payload.text ?? "");
      // Defense-in-depth — never render an empty bot bubble. If the
      // backend sends a frame with empty/whitespace text (streaming
      // chunk metadata, blank fallback, etc.), drop it on the floor
      // rather than render a styled gray oval with no content. The
      // backend should not be sending these; a console warn surfaces
      // the upstream bug for triage without breaking the UX.
      if (!text.trim()) {
        console.warn(
          "[HANA Widget] dropped empty bot_response frame",
          msg.message_id,
        );
        return;
      }
      // Flip the most-recent in-flight user bubble to "delivered"
      // (double-check) since the bot has now produced a substantive
      // reply. We don't track per-turn user-message ids here because
      // a typical session has one in-flight user message at any time;
      // walking the live region from the bottom up + matching the
      // last "sent" status is sufficient.
      this.markLatestUserMessageDelivered();
      const botTimestamp = (msg.timestamp ?? Date.now() / 1000) * 1000;
      // REQ-C-047 — pre_messages: each non-empty string renders as its
      // own bot bubble BEFORE the main text bubble, in order. They are
      // part of the SAME logical turn (no typing indicator between them,
      // they share the parent message's timestamp). Bubble ids are
      // derived from the parent message_id so reconnect-replay dedup
      // (MessageList.appendChatMessage) still de-duplicates them.
      const preMessages = this.coercePreMessages(payload.pre_messages);
      preMessages.forEach((pre, idx) => {
        this.messageList.appendChatMessage({
          id: `${msg.message_id}:pre:${idx}`,
          type: "text",
          sender: "bot",
          content: pre,
          timestamp: botTimestamp,
        });
      });
      this.messageList.appendChatMessage({
        id: msg.message_id,
        type: "text",
        sender: "bot",
        content: text,
        // REQ-C-047 — specialist photo on the main bubble (additive;
        // TextBubble drops it on a bad scheme or load error).
        photoUrl:
          typeof payload.photo_url === "string" ? payload.photo_url : undefined,
        timestamp: botTimestamp,
      });
      const suggestedActions = this.coerceSuggestedActions(
        payload.suggested_actions,
      );
      if (suggestedActions.length > 0) {
        this.showSuggestedActions(
          suggestedActions,
          this.coerceSuggestedActionsDisplay(payload.display),
        );
      } else {
        this.dismissSuggestedActions();
      }
      return;
    }
    if (msg.type === "agent_message") {
      // Issue #497 follow-up #3 — a live human agent (Chatwoot takeover)
      // is replying. Render as a left-aligned chat bubble labelled "Live
      // agent", NOT a centered grey system notice. The bot is silenced
      // while the agent owns the conversation (backend takeover gate).
      const payload = (msg.payload ?? {}) as {
        text?: string;
        agent_name?: string;
      };
      const text = String(payload.text ?? "");
      if (!text.trim()) {
        console.warn(
          "[HANA Widget] dropped empty agent_message frame",
          msg.message_id,
        );
        return;
      }
      this.markLatestUserMessageDelivered();
      // A human replying means the bot is paused — clear any stale pills.
      this.dismissSuggestedActions();
      this.messageList.appendChatMessage({
        id: msg.message_id,
        type: "text",
        sender: "agent",
        content: text,
        agentName:
          typeof payload.agent_name === "string" && payload.agent_name.trim()
            ? payload.agent_name
            : undefined,
        timestamp: (msg.timestamp ?? Date.now() / 1000) * 1000,
      });
      return;
    }
    if (msg.type === "system" || msg.type === "error") {
      // payload.text is the canonical human message (matches
      // bot_response and system shape). payload.message is the
      // pre-2026-05-05 shape for error frames; kept as a fallback
      // so a buffered legacy error replayed on reconnect still
      // renders something readable instead of an empty grey oval.
      const payload = (msg.payload ?? {}) as {
        text?: string;
        message?: string;
        event?: string;
      };
      const text = String(payload.text ?? payload.message ?? "");
      if (!text.trim()) {
        console.warn(
          "[HANA Widget] dropped empty system/error frame",
          msg.message_id,
        );
        return;
      }

      // GH #792: WhatsApp transition notice — render as a distinct
      // banner rather than a regular system bubble. After showing the
      // notice, reconnect so the next message goes to a fresh session
      // with pre-populated patient info.
      if (payload.event === "whatsapp_transitioned") {
        this.messageList.appendTransitionNotice(text);
        this.reconnectForNewSession();
        return;
      }

      this.messageList.appendChatMessage({
        id: msg.message_id,
        type: "system",
        sender: "system",
        content: text,
        timestamp: (msg.timestamp ?? Date.now() / 1000) * 1000,
      });
      return;
    }
    // REQ-A-020 / #880 — replayed transcript turns. The resume exchange
    // hands the prior conversation back as `user_message` / `bot_message`
    // frames (transport.ts::_resumeBeforeConnect, ::_tryResume). Until
    // this branch existed they fell through to the comment below and were
    // dropped silently: the transport did the work, the patient saw
    // nothing, and on the WebSocket transport the server's own buffer
    // replay masked it. Found by the first browser walk (journal 0258).
    if (msg.type === "user_message" || msg.type === "bot_message") {
      const payload = (msg.payload ?? {}) as { text?: string };
      const text = String(payload.text ?? "");
      if (!text.trim()) return;
      const isUser = msg.type === "user_message";
      // A replayed turn means this session is NOT a fresh visit, so the
      // empty-state furniture (welcome copy + starter pills) must go
      // before the transcript lands on top of it.
      this.enterResumedConversation(isUser);
      this.messageList.appendChatMessage({
        id: msg.message_id,
        type: "text",
        sender: isUser ? "user" : "bot",
        content: text,
        timestamp: (msg.timestamp ?? Date.now() / 1000) * 1000,
        // Delivered, not "sent": these turns were answered before the
        // page reloaded, so a spinner or a single check would misreport
        // history as in-flight.
        ...(isUser ? { status: "delivered" as const } : {}),
      });
      return;
    }
    // ping/pong handled at transport layer; nothing to render. Other
    // types (consent_request, feedback_request, suggested_actions,
    // form_data) flow through dedicated callbacks set by the
    // embedder via onConsentResponse / onFeedbackSubmit / etc.
  }

  /**
   * REQ-A-020 / #880 — switch the panel out of its empty state because a
   * prior conversation is being replayed into it.
   *
   * `sawUserTurn` carries the consent decision, and it is the reason this
   * is derived from the REPLAY rather than from a browser flag. The
   * opening Terms-of-Use gate is local (`handleUserSend` blocks every send
   * until "OK"), so a stored user turn coming back FROM THE SERVER is
   * proof that this same session already passed that gate. Re-asking is
   * then not a safeguard, it is the widget forgetting — which is half the
   * symptom #880 reports.
   *
   * What this deliberately does NOT do is trust `sessionStorage`. The
   * ticket alone never acknowledges anything: a ticket that fails to
   * exchange leaves the gate exactly where it was.
   */
  private enterResumedConversation(sawUserTurn: boolean): void {
    if (!this.resumedConversation) {
      this.resumedConversation = true;
      // #902: the welcome is NOT hidden here. It carries the only clickable
      // Terms of Use link the patient ever gets, and hiding it on resume made
      // the Terms unreachable after any page change. The starter pills still
      // collapse — `MessageList.appendChatMessage` dismisses them the moment
      // any message renders — so a resumed session now looks exactly like a
      // never-navigated one, which is what it is.
    }
    if (sawUserTurn && !this.openingTermsAcknowledged) {
      this.acknowledgeOpeningTerms();
    }
  }

  /**
   * REQ-A-020 / #880 — re-establish the prior conversation on MOUNT,
   * instead of waiting for the patient to speak again.
   *
   * `connect()` used to run lazily on first send. With a resume ticket in
   * hand that is too late by exactly the moment that matters: the patient
   * lands on the next page, is greeted as a stranger, is asked to accept
   * the Terms again, and sees an empty panel — the #880 symptom — and only
   * once they type does their history appear behind what they just sent.
   * Measured at 5s, 8s and 11s after load: still disconnected, no resume
   * attempted (journal 0258).
   *
   * Guarded on a ticket existing so a first-time visitor is untouched: no
   * socket is opened, and the lazy-connect path stays exactly as it was.
   * Failure is silent by design — `_resumeBeforeConnect` swallows a dead
   * or refused ticket, and the patient simply starts fresh.
   */
  restoreConversation(): void {
    if (!this.transport) return;
    if (!readTicket(this.config.entityId)) return;
    if (this.transport.state !== TransportState.DISCONNECTED) return;
    void this.transport.connect();
  }

  /**
   * Walk the message list from bottom up and flip the most-recent
   * user bubble's status indicator from "sent" → "delivered". Called
   * when the first bot frame for the current turn arrives. No-op if
   * no user bubble is found or it is already delivered.
   */
  private markLatestUserMessageDelivered(): void {
    const userBubbles = this.messageList
      .getElement()
      .querySelectorAll<HTMLElement>(".hana-bubble--user[data-message-id]");
    if (userBubbles.length === 0) return;
    const latest = userBubbles[userBubbles.length - 1];
    const id = latest.getAttribute("data-message-id");
    if (!id) return;
    this.messageList.setUserMessageStatus(id, "delivered");
  }

  /**
   * REQ-C-033 — handle an inbound `form` frame.
   *
   * Wire shape (Shard-1a transport envelope): the field list arrives
   * nested under `msg.payload`:
   *   { type:"form", payload:{ fields:[...], title?, submitLabel?,
   *     cancelLabel? }, message_id, timestamp, metadata }
   * We UNWRAP `payload` into the flat `FormMessage` the dispatcher +
   * InlineForm consume: `{ type:"form", ...payload }`.
   *
   * Dispatch:
   *   - decideFormDisposition(form, config) === "form"
   *       → instantiate an InlineForm (pre-filled values editable) and
   *         append it to the message list (MessageList.appendForm).
   *   - === "conversational"
   *       → render fieldsToConversationalPrompts() as bot bubbles
   *         (one bubble per field; backend accepts per-field answers).
   *
   * Replay dedup (mirrors MessageList.appendChatMessage):
   *   - a form frame whose message_id already rendered is dropped.
   *   - a form frame whose message_id was already SUBMITTED renders
   *     disabled (no double-submit after reconnect).
   */
  private handleFormMessage(msg: WireChatMessage): void {
    const messageId = msg.message_id;

    // A form already submitted this session, replayed on reconnect:
    // re-render it disabled so the patient cannot resubmit.
    if (messageId && this.submittedFormIds.has(messageId)) {
      const form = this.unwrapFormMessage(msg);
      this.renderInlineForm(form, messageId, { disabled: true });
      return;
    }

    // Already rendered (and not submitted) — silently drop the replay.
    if (messageId && this.renderedFormIds.has(messageId)) {
      return;
    }
    if (messageId) {
      this.renderedFormIds.add(messageId);
    }

    const form = this.unwrapFormMessage(msg);
    const disposition = decideFormDisposition(form, this.config);

    if (disposition === "conversational") {
      // One bot bubble per field. The bubble ids are derived from the
      // parent message_id so reconnect-replay dedup
      // (MessageList.appendChatMessage) de-duplicates them too.
      const botTimestamp = (msg.timestamp ?? Date.now() / 1000) * 1000;
      const prompts = fieldsToConversationalPrompts(form.fields);
      prompts.forEach((prompt, idx) => {
        this.messageList.appendChatMessage({
          id: messageId ? `${messageId}:field:${idx}` : generateMessageId(),
          type: "text",
          sender: "bot",
          content: prompt,
          timestamp: botTimestamp,
        });
      });
      return;
    }

    this.renderInlineForm(form, messageId, { disabled: false });
  }

  /**
   * Unwrap the Shard-1a transport envelope into the flat FormMessage
   * the dispatcher + InlineForm consume. `payload` carries the field
   * list + optional labels/title; we spread it over the `type:"form"`
   * discriminant. `fields` is normalised to an array so a malformed
   * frame degrades to an empty (→ conversational) form rather than
   * throwing inside the dispatcher.
   */
  private unwrapFormMessage(msg: WireChatMessage): FormMessage {
    const payload = (msg.payload ?? {}) as {
      fields?: unknown;
      title?: unknown;
      submitLabel?: unknown;
      cancelLabel?: unknown;
    };
    const fields = Array.isArray(payload.fields)
      ? (payload.fields as FormFieldDefinition[])
      : [];
    return {
      type: "form",
      fields,
      title: typeof payload.title === "string" ? payload.title : undefined,
      submitLabel:
        typeof payload.submitLabel === "string"
          ? payload.submitLabel
          : undefined,
      cancelLabel:
        typeof payload.cancelLabel === "string"
          ? payload.cancelLabel
          : undefined,
    };
  }

  /**
   * Instantiate an InlineForm wired to submit/cancel callbacks and
   * append it to the message list. When `disabled` is true (a replayed
   * already-submitted frame) the form is appended then immediately
   * driven to its submitted state so no input is editable.
   */
  private renderInlineForm(
    form: FormMessage,
    messageId: string | undefined,
    opts: { disabled: boolean },
  ): void {
    const inlineForm = new InlineForm({
      config: this.config,
      message: form,
      callbacks: {
        onSubmit: (event: FormSubmitEvent) =>
          this.handleFormSubmit(event, messageId),
        onCancel: () => this.handleFormCancel(),
      },
    });
    this.messageList.appendForm(inlineForm);

    if (opts.disabled) {
      // Replayed-after-submit: render the form non-interactive. The
      // form's own submit handler is the only mutation path; clicking a
      // disabled submit button is a no-op, but we additionally stamp the
      // disabled attribute so the DOM reflects the submitted state for
      // assertion + screen readers.
      const el = inlineForm.getElement();
      el.setAttribute("data-form-disabled", "true");
      el.querySelectorAll<HTMLButtonElement>(
        "button, input, select, textarea",
      ).forEach((node) => {
        node.disabled = true;
      });
    }
  }

  /**
   * REQ-C-033 — the patient submitted the inline form.
   *
   * 1. Send the FORM_DATA wire frame on the ACTIVE transport (works for
   *    both WS and REST — same `transport.send` path user_message uses):
   *      { type:"form_data", payload:<flat field map>, message_id, ... }
   *    The payload IS `event.data` (the flat field→value map). entityId
   *    + timestamp on FormSubmitEvent are dropped deliberately — the
   *    backend's `_normalize_form_data` consumes only the field map.
   * 2. Render a patient-side acknowledgement bubble summarising the
   *    submitted fields with any NRIC-typed value masked (S****999P).
   * 3. Record the message_id as submitted so a replayed frame renders
   *    disabled (double-submit guard across reconnect).
   *
   * Scope note: this pins the EMITTED bytes only. The end-to-end
   * round-trip against the real backend FORM_DATA branch is proven in
   * Shard 4's full-journey Tier-2, not here.
   */
  private handleFormSubmit(
    event: FormSubmitEvent,
    messageId: string | undefined,
  ): void {
    if (messageId) {
      this.submittedFormIds.add(messageId);
    }

    const wire: WireChatMessage = {
      type: "form_data",
      payload: { ...event.data },
      message_id: generateMessageId(),
      timestamp: Date.now() / 1000, // backend protocol: unix epoch SECONDS
      metadata: {},
    };

    if (this.transport) {
      if (this.transport.state === TransportState.DISCONNECTED) {
        void this.transport.connect();
      }
      void this.transport.send(wire).catch((err) => {
        console.error("[HANA Widget] form_data send failed", err);
      });
    }

    // Patient-side acknowledgement bubble, NRIC masked at display.
    this.messageList.appendChatMessage({
      id: generateMessageId(),
      type: "text",
      sender: "user",
      content: buildFormAckSummary(event.data),
      timestamp: Date.now(),
      status: "sent",
    });
  }

  /**
   * REQ-C-033 — the patient cancelled the inline form. Send a plain
   * "cancel" user text turn (the backend treats it as a normal message
   * the LLM can act on) and collapse the form. The InlineForm already
   * disabled itself + scrubbed any NRIC field on cancel.
   */
  private handleFormCancel(): void {
    void this.handleUserSend("cancel");
  }

  /**
   * Show transient connection-status messages. Production should
   * surface these as a status pill in the header (out of scope for
   * the M01-T02 wiring); for now we drop them silently — the
   * transport's internal queue + reconnect logic is the durable path.
   */
  private handleTransportState(_state: TransportState): void {
    // No-op for now. Reserved for future header connection-status UI.
  }

  /** Public accessor for the chat input component. */
  get chatInput(): ChatInput {
    return this._chatInput;
  }

  /**
   * Get the root container element for mounting into Shadow DOM.
   */
  getElement(): HTMLElement {
    return this.container;
  }

  /**
   * Open the chat panel.
   */
  open(): void {
    if (this.state === "open") return;
    this.state = "open";
    // REQ-A-020 / #880 — remember the panel was open so a page navigation
    // restores it. Without this the transcript is replayed into a CLOSED
    // panel and the patient has to click the bubble to discover their
    // conversation survived, which is half the reported symptom.
    storePanelState(this.config.entityId, true);
    this.panelEl.classList.add("hana-panel--open");
    this.panelEl.setAttribute("aria-hidden", "false");
    this.toggle.setOpen(true);
    // REQ-D-046 — modal mode reveals the backdrop in the same frame
    // the panel becomes visible; the backdrop carries the dim overlay
    // + acts as a click-to-close target.
    if (this.modalBackdrop) {
      this.modalBackdrop.classList.add("hana-modal-backdrop--open");
      this.modalBackdrop.setAttribute("aria-hidden", "false");
    }

    // Focus the input after the animation completes
    requestAnimationFrame(() => {
      this._chatInput.focus();
    });

    document.addEventListener("keydown", this.handleKeyDown);
    this.panelEl.addEventListener("keydown", this.trapFocus);
  }

  /**
   * Close the chat panel.
   */
  close(): void {
    if (this.state === "closed") return;
    this.state = "closed";
    storePanelState(this.config.entityId, false);
    this.panelEl.classList.remove("hana-panel--open");
    this.panelEl.setAttribute("aria-hidden", "true");
    this.toggle.setOpen(false);
    if (this.modalBackdrop) {
      this.modalBackdrop.classList.remove("hana-modal-backdrop--open");
      this.modalBackdrop.setAttribute("aria-hidden", "true");
    }

    document.removeEventListener("keydown", this.handleKeyDown);
    this.panelEl.removeEventListener("keydown", this.trapFocus);

    // REQ-D-046 — restore focus to the host-page trigger CTA per
    // WCAG 2.4.3 focus order (works in both modal and panel modes).
    // Falls back to the floating-bubble launcher when no custom
    // trigger is wired.
    if (this.modalTrigger) {
      this.modalTrigger.focus();
    } else {
      this.toggle.getElement().focus();
    }
  }

  /**
   * Toggle between open and closed.
   */
  togglePanel(): void {
    if (this.state === "open") {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Get the current widget state.
   */
  getState(): WidgetState {
    return this.state;
  }

  /**
   * Get the current viewport mode.
   */
  getViewportMode(): ViewportMode {
    return this.viewportMode;
  }

  /**
   * Clean up event listeners, media query observers, and the
   * underlying transport (M01-T02). Once destroyed, the widget
   * cannot be reused — HanaChatbotElement constructs a fresh
   * ChatWidget on attribute change.
   */
  destroy(): void {
    this.mediaQuery.removeEventListener("change", this.handleViewportChange);
    document.removeEventListener("keydown", this.handleKeyDown);
    this.panelEl.removeEventListener("keydown", this.trapFocus);
    // Stop the relative-timestamp ticker so its setInterval handle
    // doesn't outlive the chat session.
    this.messageList.destroy();
    if (this.transport) {
      this.transport.disconnect();
      this.transport = null;
    }
    // REQ-D-046 — release the trigger CTA listener so a widget
    // teardown (e.g. attribute change re-mount) doesn't leave a
    // dangling click handler on the host page.
    if (this.triggerCtaEl && this.boundTriggerHandler) {
      this.triggerCtaEl.removeEventListener("click", this.boundTriggerHandler);
      this.triggerCtaEl = null;
      this.boundTriggerHandler = null;
    }
    this.modalTrigger = null;
    this.modalBackdrop = null;
  }

  /**
   * REQ-D-046 — current size state. Public for tests + admin
   * inspection.
   */
  getSize(): WidgetSize {
    return this.size;
  }

  /**
   * REQ-D-046 — apply a new size state. Updates panel + header
   * CSS classes, mirrors the size to the header chrome, and
   * persists when the embedder opted into allowExpand. Modal mode
   * is a no-op — modals don't have a size axis.
   */
  setSize(size: WidgetSize): void {
    if (this.config.uiMode !== "panel") return;
    if (this.size === size) return;
    this.panelEl.classList.remove(`hana-panel--size-${this.size}`);
    this.panelEl.classList.add(`hana-panel--size-${size}`);
    this.size = size;
    this.header.setSize(size);
    if (this.config.allowExpand) this.persistSize(size);
  }

  /**
   * REQ-D-046 — flip between compact and expanded. Fullscreen is
   * reserved for keyboard-aware mobile form mode; the user toggle
   * never produces it.
   */
  toggleSize(): void {
    if (this.size === "expanded") this.setSize("compact");
    else this.setSize("expanded");
  }

  /**
   * REQ-D-046 — persistence key. Scoped to the entity so a tester
   * navigating between /uat-sites/<entity>/ pages doesn't inherit
   * the wrong footprint.
   */
  /**
   * REQ-A-020 / #880 — reopen the panel if it was open when the patient
   * navigated. Per-tab (`sessionStorage`), so it never leaks into a later
   * visit; a closed panel resumes silently and the transcript is waiting
   * when they click.
   */
  restorePanelState(): void {
    if (readPanelState(this.config.entityId) === "open") this.open();
  }

  private persistenceKey(): string {
    return `hana-widget-size-${this.config.entityId}`;
  }

  /**
   * REQ-D-046 — write the size to localStorage. Swallows storage
   * errors (private-mode browsers, quota exceeded) — persistence
   * is best-effort and MUST NOT crash the widget.
   */
  private persistSize(size: WidgetSize): void {
    try {
      window.localStorage.setItem(this.persistenceKey(), size);
    } catch {
      // Best-effort; quota / disabled storage / SSR.
    }
  }

  /**
   * REQ-D-046 — restore a previously persisted size. Returns null
   * (caller falls back to config.defaultSize) when storage is
   * unavailable OR the persisted value is no longer a valid
   * WidgetSize.
   */
  private restorePersistedSize(): WidgetSize | null {
    try {
      const value = window.localStorage.getItem(this.persistenceKey());
      if (
        value === "compact" ||
        value === "expanded" ||
        value === "fullscreen"
      ) {
        return value;
      }
    } catch {
      // Best-effort.
    }
    return null;
  }

  /**
   * Get the current PDPA consent status for this session.
   */
  getConsentStatus(): ConsentStatus {
    return this.consentStatus;
  }

  /**
   * Register callbacks for consent events.
   * Called by the communication layer (M01-T02) to wire up consent handling.
   */
  onConsentResponse(callbacks: ConsentCallbacks): void {
    this.consentCallbacks = callbacks;
  }

  /**
   * Handle a consent_request message from the backend.
   * Creates and renders a ConsentPrompt in the message list.
   * If consent has already been granted or a prompt is already visible,
   * this call is ignored.
   */
  handleConsentRequest(request?: ConsentRequestMessage): void {
    // Do not re-prompt if already consented in this session
    if (this.consentStatus !== "pending") return;

    const prompt = new ConsentPrompt({
      config: this.config,
      callbacks: {
        onConsent: (event: ConsentEvent) => {
          this.consentStatus = "granted";
          this.consentCallbacks?.onConsent(event);
        },
        onDecline: (event: ConsentEvent) => {
          this.consentStatus = "declined";
          this.consentCallbacks?.onDecline(event);
        },
      },
      message: request?.message,
      touUrl: request?.touUrl,
    });

    this.messageList.appendConsentPrompt(prompt);
  }

  /**
   * Register a callback for when a suggested action pill is clicked.
   * Called by the communication layer (M01-T02) to wire up pill actions.
   */
  onSuggestedAction(callback: (action: string) => void): void {
    this.suggestedActionCallback = callback;
  }

  /**
   * Register a callback for suggested action analytics events.
   * Called by the admin dashboard integration (M06-T08).
   */
  onSuggestedActionAnalytics(
    callback: (event: SuggestedActionAnalyticsEvent) => void,
  ): void {
    this.suggestedActionAnalyticsCallback = callback;
  }

  /**
   * Set the confidence threshold for displaying suggested action pills.
   * Actions below this threshold are filtered out.
   */
  setSuggestedActionsThreshold(threshold: number): void {
    this.suggestedActionsThreshold = threshold;
  }

  /**
   * Display suggested action pills from a backend payload.
   * Filters by confidence threshold, renders pills below the latest
   * bot message, and wires up pill click to emit the action text.
   * If pills are already showing, they are replaced.
   *
   * Returns the number of pills actually displayed (after filtering).
   */
  showSuggestedActions(
    actions: SuggestedAction[],
    display: SuggestedActionsDisplay = "pills",
  ): number {
    // Dismiss any existing pills
    this.dismissSuggestedActions();

    const suggestedActions = new SuggestedActions({
      config: this.config,
      confidenceThreshold: this.suggestedActionsThreshold,
      display,
      onAction: (action: string, label: string) => {
        // REQ-D-029: pill click → send through the same transport
        // path as typed input. Opaque action tokens use source="pill"
        // so the backend can apply the server-side allowlist. GH #560
        // (S4b): doctor-selection tokens (`doctor_select:<gid>` +
        // the reserved `doctor_select:back`) are opaque machine tokens
        // too — the server gate validates their identifier shape
        // (hana.security.pill_action_tokens.is_allowed_pill_action).
        // Legacy natural-language payloads are sent as typed text;
        // sending them as pill tokens makes the WebSocket gate reject
        // actions the widget itself rendered.
        const source =
          ChatWidget.OPAQUE_PILL_ACTION_TOKENS.has(action) ||
          action.startsWith("doctor_select:")
            ? "pill"
            : "typed";
        void this.handleUserSend(action, source, label);
        // Preserve the external observer hook (M01-T02 wiring).
        this.suggestedActionCallback?.(action);
      },
      onAnalytics: (event: SuggestedActionAnalyticsEvent) => {
        this.suggestedActionAnalyticsCallback?.(event);
      },
    });

    const count = suggestedActions.renderActions(actions);

    if (count === 0) {
      // No pills passed the threshold — do not add to DOM
      return 0;
    }

    this.suggestedActions = suggestedActions;
    this.messageList.appendMessage(suggestedActions.getElement());

    return count;
  }

  /**
   * REQ-C-047 — normalize the optional `pre_messages` extra into an
   * ordered list of non-empty strings. Tolerates absent/null/non-array
   * payloads (returns []) and drops any entry that is not a non-empty
   * string, so an old payload shape can never render a blank bubble.
   */
  private coercePreMessages(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    // Defensive bound (security H-1, 2026-06-07): pre_messages originate
    // from an external upstream (Phase 1). The server bounds them at its
    // trust boundary; this widget-side cap is defense-in-depth so a stale
    // or bypassed server bundle can never flood the chat pane with
    // unbounded bubbles or megabyte strings.
    const MAX_PRE_MESSAGES = 10;
    const MAX_PRE_MESSAGE_CHARS = 4096;
    return value
      .flatMap((item): string[] => {
        if (typeof item !== "string") return [];
        const trimmed = item.trim();
        return trimmed ? [item.slice(0, MAX_PRE_MESSAGE_CHARS)] : [];
      })
      .slice(0, MAX_PRE_MESSAGES);
  }

  private coerceSuggestedActions(value: unknown): SuggestedAction[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap((item): SuggestedAction[] => {
      if (!item || typeof item !== "object") return [];
      const candidate = item as Record<string, unknown>;
      const label = candidate.label;
      const action = candidate.action ?? candidate.value;
      const confidence =
        typeof candidate.confidence === "number" ? candidate.confidence : 1;
      if (typeof label !== "string" || typeof action !== "string") {
        return [];
      }
      // GH #560 (S4b) — doctor-pill metadata passthrough (§4.2 bubble
      // contract). Only the two known string fields survive coercion;
      // anything else in metadata is dropped at this trust boundary.
      let metadata: SuggestedAction["metadata"];
      if (candidate.metadata && typeof candidate.metadata === "object") {
        const raw = candidate.metadata as Record<string, unknown>;
        const doctorGivenId =
          typeof raw.doctor_given_id === "string"
            ? raw.doctor_given_id
            : undefined;
        const capabilityTag =
          typeof raw.capability_tag === "string"
            ? raw.capability_tag
            : undefined;
        if (doctorGivenId !== undefined || capabilityTag !== undefined) {
          metadata = {
            doctor_given_id: doctorGivenId,
            capability_tag: capabilityTag,
          };
        }
      }
      return [
        {
          label,
          action,
          confidence,
          sparkle: candidate.sparkle === true,
          ...(metadata ? { metadata } : {}),
        },
      ];
    });
  }

  private coerceSuggestedActionsDisplay(
    value: unknown,
  ): SuggestedActionsDisplay {
    return value === "list" ? "list" : "pills";
  }

  /**
   * Dismiss (remove) the currently displayed suggested action pills.
   * Called when the user sends a typed message or when new pills arrive.
   */
  dismissSuggestedActions(): void {
    if (this.suggestedActions) {
      this.suggestedActions.dismiss();
      const el = this.suggestedActions.getElement();
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
      this.suggestedActions = null;
    }
  }

  /**
   * Get the current SuggestedActions instance (for testing).
   */
  getSuggestedActions(): SuggestedActions | null {
    return this.suggestedActions;
  }

  /**
   * Register a callback for feedback submission events.
   * Called by the communication layer (M01-T02) to wire up feedback handling.
   */
  onFeedbackSubmit(callbacks: FeedbackCallbacks): void {
    this.feedbackCallbacks = callbacks;
  }

  /**
   * Handle a feedback_request message from the backend.
   * Creates and renders a FeedbackCapture card in the message list.
   */
  handleFeedbackRequest(request: FeedbackRequestMessage): void {
    const feedback = new FeedbackCapture({
      config: this.config,
      conversationId: request.conversationId,
      prompt: request.prompt,
      callbacks: {
        onSubmit: (event: FeedbackEvent) => {
          this.feedbackCallbacks?.onSubmit(event);
        },
      },
    });

    this.messageList.appendMessage(feedback.getElement());
  }

  private static readonly FOCUSABLE_SELECTOR =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  private trapFocus(e: KeyboardEvent): void {
    if (e.key !== "Tab") return;

    const focusableEls = this.panelEl.querySelectorAll<HTMLElement>(
      ChatWidget.FOCUSABLE_SELECTOR,
    );
    if (focusableEls.length === 0) return;

    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    if (e.shiftKey) {
      // Shift+Tab: wrap from first to last
      if (
        document.activeElement === firstEl ||
        !this.panelEl.contains(document.activeElement)
      ) {
        e.preventDefault();
        lastEl.focus();
      }
    } else {
      // Tab: wrap from last to first
      if (
        document.activeElement === lastEl ||
        !this.panelEl.contains(document.activeElement)
      ) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.state === "open") {
      e.preventDefault();
      this.close();
    }
  }

  private handleViewportChange(e: MediaQueryListEvent): void {
    this.viewportMode = e.matches ? "mobile" : "desktop";
    this.updateViewportClass();
  }

  private updateViewportClass(): void {
    if (this.viewportMode === "mobile") {
      this.hostEl.classList.add("hana-mobile");
    } else {
      this.hostEl.classList.remove("hana-mobile");
    }
  }

}

/**
 * Stable per-message identifier. Uses crypto.randomUUID when available
 * (modern browsers + Node 19+) and falls back to a timestamp+random
 * shape otherwise. The ID is opaque to the backend — used only for
 * correlating optimistic UI bubbles with server acks.
 */
function generateMessageId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
